/**
 * Removes credit card numbers from text
 * Detects various credit card formats including:
 * - 16 digits with spaces: 1234 5678 9012 3456
 * - 16 digits with hyphens: 1234-5678-9012-3456
 * - 16 digits continuous: 1234567890123456
 * - 15 digits (Amex): 371449635398431
 * @param text - The text to sanitize
 * @returns The sanitized text with credit card numbers removed
 */
export function maskCreditCardNumbers(text: string | null | undefined): string {
  if (!text) return text ?? ``

  // Regex patterns for different credit card formats
  const patterns = [
    // 16 digits with spaces
    /\b\d{4}\s\d{4}\s\d{4}\s\d{4}\b/g,
    // 16 digits with hyphens
    /\b\d{4}-\d{4}-\d{4}-\d{4}\b/g,
    // 15-16 continuous digits (handles Amex and others)
    /\b\d{15,16}\b/g,
    // 13-19 digits with optional spaces/hyphens between groups
    /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{3,6}\b/g,
  ]

  let sanitized = text
  patterns.forEach((pattern) => {
    sanitized = sanitized.replace(pattern, (match) => {
      // Remove credit card numbers entirely
      const digitsOnly = match.replace(/\D/g, ``)
      if (digitsOnly.length >= 13) {
        return ``
      }
      return match
    })
  })

  return sanitized
}

/**
 * Removes SSNs from text
 * Detects various SSN formats including:
 * - 9 digits with hyphens: 123-45-6789
 * - 9 digits with spaces: 123 45 6789
 * - 9 continuous digits: 123456789
 * @param text - The text to sanitize
 * @returns The sanitized text with SSNs removed
 */
export function maskSSNs(text: string | null | undefined): string {
  if (!text) return text ?? ``

  const patterns = [
    // SSN with hyphens: 123-45-6789
    /\b\d{3}-\d{2}-\d{4}\b/g,
    // SSN with spaces: 123 45 6789
    /\b\d{3}\s\d{2}\s\d{4}\b/g,
    // 9 continuous digits (be careful not to catch other numbers)
    /\b\d{9}\b/g,
  ]

  let sanitized = text
  patterns.forEach((pattern) => {
    sanitized = sanitized.replace(pattern, (match) => {
      const digitsOnly = match.replace(/\D/g, ``)
      if (digitsOnly.length === 9) {
        return ``
      }
      return match
    })
  })

  return sanitized
}

/**
 * Removes both credit card numbers and SSNs from text
 * @param text - The text to sanitize
 * @returns The sanitized text with sensitive data removed
 */
export function maskSensitiveData(text: string | null | undefined): string {
  if (!text) return text ?? ``
  return maskSSNs(maskCreditCardNumbers(text))
}
